import Vue from 'vue'
import Storage from '@/utils/storage'
import router from '@/plugins/router'
import CheckoutSessionAPI from '@/api/session'
import Seon from '@/utils/seon'
import PAYMENT_TYPES from '@/constants/payment-types'
import PAYMENT_METHODS from '@/constants/payment-methods'
import {
  getCheckoutId,
  setCheckoutId,
  removeCheckoutId,
  getDeviceId,
  setDeviceId,
} from '@/utils/auth'
import logger from '@/utils/logger'
import clientLogger from '@/plugins/logger/core'
import { TRACKING_EVENTS } from '@/constants/tracking'
import { ApplicationError, HttpRequestError } from '@/utils/error'
import { logSessionReplayGateStatus } from '@/plugins/abt'

const DEFAULT_NUM_OF_INSTALMENTS = 3

const statuses = {
  UNCHECK: 'uncheck',
  CHECKED: 'checked',
  VALID: 'valid',
  INVALID: 'invalid',
}

const state = {
  status: statuses.UNCHECK,
  order: Storage.getSessionData() || {},
  checkoutId: getCheckoutId() || '',
  deviceId: getDeviceId() || '',
  seonData: Seon.getSeonData() || {},
  autofillPhoneNumber: null,
  checkingSession: false,
  checked: {},
  redirectUrl: '',
  sessionErrorData: {},
  preventSmsOnLanguageSwitch: false,
}

const getters = {
  order(state) {
    return state.order || {}
  },
  orderCurrency(_, getters) {
    const { totalAmount = {} } = getters.order

    return totalAmount.currency || 'SAR'
  },
  phone(_, getters) {
    const { phoneNumber = {} } = getters.order

    return {
      countryCode: phoneNumber.countryCode || '',
      phoneNumber: phoneNumber.nationalNumber || '',
    }
  },
  phoneNumberInternational(_, getters) {
    const { phoneNumber = {} } = getters.order

    return phoneNumber.internationalNumber || ''
  },
  cashbackEnabled(state) {
    const payNowCashbackPercentage =
      state.order?.payNowConfigs?.payNowCashbackPercentage ?? 0

    return payNowCashbackPercentage > 0
  },
  skipCvv(state, getters, rootState, rootGetters) {
    const selectedCard = rootState.instalment?.selectedCard || {}
    const cardForVerification = rootState.instalment?.cardForVerification
    const isCardsV2CvvDetailsV2Enabled =
      rootGetters['configuration/isCardsV2CvvDetailsV2Enabled']

    if (cardForVerification) {
      // We already provide CCV during re-adding card process
      return true
    }

    if (isCardsV2CvvDetailsV2Enabled) {
      return selectedCard?.isDisabledCvv ?? false
    }

    if (selectedCard?.paymentMethod === PAYMENT_METHODS.MADA_CARD) {
      return false
    }

    if (getters.paymentType === PAYMENT_TYPES.PAY_NOW) {
      return state.order?.payNowConfigs?.payNowCvvDisabled ?? false
    }

    return false
  },
  cancelUrl(_, getters) {
    const { cancelUrl = '' } = getters.order

    return cancelUrl
  },
  checkoutId(state) {
    return state.checkoutId || ''
  },
  deviceId(state) {
    return state.deviceId || ''
  },
  autofillPhoneNumber(_, getters) {
    // If we have a config from application then we use it
    if (TypeChecker.isBoolean(state.autofillPhoneNumber)) {
      return state.autofillPhoneNumber
    }

    // Otherwise we use config from order that is a configuration in BE side
    // Only disable autofill when BE returns false. Other cases will be treated as true
    return (
      getters.order?.autofillPhoneNumber !== false ||
      getters.order?.leavePhoneNumberEmptyForNewCustomers === true
    )
  },
  leavePhoneNumberEmptyForNewCustomers(_, getters) {
    return getters.order?.leavePhoneNumberEmptyForNewCustomers ?? false
  },
  phoneNumberUpdated(state) {
    return state.order?.phoneNumberUpdated ?? false
  },
  paymentType(_, getters) {
    return getters.order.paymentType
  },
  numberOfInstallment(_, getters) {
    return getters.order.instalments || DEFAULT_NUM_OF_INSTALMENTS // Default instalment is 3
  },
  isPaylaterOrder(_, getters) {
    return getters.paymentType === PAYMENT_TYPES.PAY_BY_LATER
  },
  isInstalmentOrder(_, getters) {
    return getters.paymentType === PAYMENT_TYPES.PAY_BY_INSTALMENTS
  },
  isPayInXOrder(_, getters) {
    // Pay in X when instalment greater than 4
    return getters.isInstalmentOrder && getters.numberOfInstallment > 4
  },
  isPayNextMonthOrder(_, getters) {
    return getters.paymentType === PAYMENT_TYPES.PAY_NEXT_MONTH
  },
  isPayNowOrder(_, getters) {
    return getters.paymentType === PAYMENT_TYPES.PAY_NOW
  },
  singleCheckoutRevampEnabled(state) {
    return state.order?.singleCheckoutRevampEnabled ?? false
  },
  preventSmsOnLanguageSwitch(state) {
    return state.order?.preventSmsOnLanguageSwitch ?? false
  },
  merchantPlatform(_, getters) {
    const { platform = '' } = getters.order

    if (typeof platform !== 'string') {
      return ''
    }

    return platform.toLowerCase()
  },
  merchantName(_, getters) {
    const { merchantName = '' } = getters.order

    if (typeof merchantName !== 'string') {
      return ''
    }

    return merchantName
  },
  merchantLogo(_, getters) {
    const { merchantLogo = '' } = getters.order

    if (typeof merchantLogo !== 'string') {
      return ''
    }

    return merchantLogo
  },
  merchantCategory(_, getters) {
    const { merchantCategories = [] } = getters.order

    if (
      !TypeChecker.isArray(merchantCategories) ||
      merchantCategories.length === 0
    ) {
      return ''
    }

    const res = merchantCategories.map((cat) => {
      if (!cat) {
        return ''
      }

      if (Object.keys(cat).length === 0) {
        return ''
      } else {
        return cat?.name ?? ''
      }
    })

    return res.join(' ').trim()
  },
  merchantCountry(_, getters) {
    const { merchantCountry = '' } = getters.order

    if (typeof merchantCountry !== 'string') {
      return ''
    }

    return merchantCountry
  },
  merchantId(_, getters) {
    const { merchantId = '' } = getters.order

    if (typeof merchantId !== 'string') {
      return ''
    }

    return merchantId
  },
  shippingCity(_, getters) {
    const { orderShippingCity } = getters.order
    if (typeof orderShippingCity !== 'string') {
      return ''
    }

    return orderShippingCity
  },
  shippingCountry(_, getters) {
    const { orderShippingCountry } = getters.order
    if (typeof orderShippingCountry !== 'string') {
      return ''
    }

    return orderShippingCountry
  },
  isNamshiOrder(_, getters) {
    return getters.merchantName.toLowerCase() === 'namshi'
  },
  countryCode(_, getters) {
    return getters.order?.countryCode || ''
  },
  isEnabledAmexCard(_, getters) {
    return getters.countryCode.toLowerCase() === 'sa'
  },
  orderTotalAmount(_, getters) {
    return getters.order.totalAmount
  },
  orderTotalAmountWithFee(_, getters, rootState) {
    const customerFee =
      rootState?.checkout?.data?.paymentPlan?.customerServiceFee?.amount ?? 0

    if (customerFee <= 0) {
      return getters.orderTotalAmount
    }

    return {
      amount: getters.orderTotalAmount.amount + customerFee,
      currency: getters.orderTotalAmount.currency,
    }
  },
  orderDownPaymentAmount(_, getters) {
    return getters.order?.paymentPlan?.downpayment || {}
  },
  fromPaymentLink(_, getters) {
    return !!getters.order?.fromPaymentLink
  },
  // orderPlatform = in_store mean from payment link and in-store qr
  isInStore(_, getters) {
    return getters.order?.orderPlatform === 'in_store'
  },
  singleCheckoutEnabled(_, getters) {
    return !!getters.order?.singleCheckoutEnabled
  },
  isPhoneNumberOptional(_, getters) {
    return getters?.autofillPhoneNumber === false
  },
  orderId(_, getters) {
    return getters.order?.orderId || ''
  },
  cashbackDiscountAmount(_, getters) {
    return getters?.order?.payNowConfigs?.payNowCashbackValue ?? -1
  },
  seonData() {
    return state.seonData || {}
  },
  redirectUrl() {
    return state.redirectUrl || ''
  },
  sessionErrorData() {
    return state.sessionErrorData || {}
  },
  hideCloseButton(state) {
    return state.order?.hideCloseButton ?? false
  },
  mfaEnabled(state) {
    return state.order?.mfaApisEnabled ?? false
  },
  authenticationServiceLoginEnabled(state) {
    return state.order?.authenticationServiceLogin ?? false
  },
}

const mutations = {
  SET_STATUS: (state, newValue) => {
    state.status = newValue
  },
  SET_CHECKOUT_ID: (state, newValue) => {
    state.checkoutId = newValue
  },
  SET_DEVICE_ID: (state, newValue) => {
    state.deviceId = newValue
  },
  SET_ORDER: (state, newValue) => {
    state.order = { ...state.order, ...newValue }
  },
  SET_SEON_DATA: (state, newValue = {}) => {
    state.seonData = newValue
  },
  SET_AUTO_FILL_PHONE_NUMBER: (state, newValue) => {
    state.autofillPhoneNumber = newValue
  },
  SET_CHECKING_SESSION: (state, value) => {
    state.checkingSession = value
  },
  SET_CHECKED_SESSION: (state, checkoutId) => {
    state.checked[checkoutId] = true
  },
  SET_REDIRECT_URL: (state, redirectUrl) => {
    state.redirectUrl = redirectUrl
  },
  SET_SESSION_ERROR_DATA(state, value) {
    state.sessionErrorData = value
  },
}

const actions = {
  // checkSession
  async checkSession({ commit, dispatch, state, rootGetters }, data = {}) {
    // Receive checkoutId from arguments or state (Load from storage before that)
    const checkoutId = data.checkoutId || state.checkoutId

    try {
      if (state.checked[checkoutId]) {
        commit('SET_STATUS', statuses.VALID)

        return state.order
      }

      if (!checkoutId) {
        router.replace({ name: '404' })
        // Throw Error and clear checkoutId
        throw new ApplicationError('The checkout ID was not found.')
      }

      // Save/Update checkoutId
      dispatch('setCheckoutId', checkoutId)

      commit('SET_CHECKING_SESSION', true)
      commit('SET_CHECKED_SESSION', checkoutId)

      const { data: orderInfo, headers = {} } =
        await CheckoutSessionAPI.checkSession(checkoutId)

      // reset SEON data
      dispatch('setSeonData', {})

      // get existed device id
      let xDeviceId = rootGetters.deviceId
      const { xDeviceId: headerXDeviceId } = headers

      if (!xDeviceId && headerXDeviceId) {
        xDeviceId = headerXDeviceId

        dispatch('setDeviceId', xDeviceId)
      }

      const { locale = 'ar_SA', token, fromPaymentLink } = orderInfo || {}

      if (!rootGetters['application/setLocale']) {
        await dispatch('application/setI18nMessageByLocale', locale, {
          root: true,
        })
      }
      if (orderInfo?.leavePhoneNumberEmptyForNewCustomers) {
        commit('SET_AUTO_FILL_PHONE_NUMBER', true)
      }

      commit('SET_CHECKING_SESSION', false)
      commit('SET_STATUS', statuses.VALID)
      // TODO: Will remove this log after resolve the bug
      logger.debug(
        'Set checkoutID',
        { data: { checkoutId } },
        {
          category: 'session',
          'order.checkoutId': checkoutId,
          'order.id': orderInfo?.orderId ?? null,
        }
      )

      Vue?.prototype?.$trackingService?.TrackEvent(
        TRACKING_EVENTS.FETCH_CHECKOUT_CONFIGURATION.EVENT_NAME,
        { checkout_id: checkoutId }
      )

      // Dispatch fetch checkout configurations if it's not fetched yet. If it's fetched, it will return the cached data, so we it will not fetch again
      dispatch(
        'configuration/fetchCheckoutConfigurations',
        { countryCode: orderInfo.countryCode, checkoutId },
        {
          root: true,
        }
      )

      const merchantName = (orderInfo && orderInfo.merchantName) || ''

      if (merchantName.toLowerCase() === 'namshi' || fromPaymentLink) {
        dispatch('auth/setAutoSubmitPhone', true, { root: true })
      }

      if (token) {
        dispatch(
          'auth/setCustomerToken',
          { token, source: 'auto' },
          { root: true }
        )
      }

      dispatch('setOrder', orderInfo)

      // get SEON session and store
      const { seonEnabled = false } = orderInfo
      if (seonEnabled) {
        // store 30mins
        Seon.getSeonSession()
          .then((data) => {
            if (data) {
              dispatch('setSeonData', { seonEnabled, seonSession: data })
            } else {
              dispatch('setSeonData', {})
              logger.error(
                'Failed to retrieve SEON session data.',
                {
                  orderInfo,
                },
                { category: 'seon' }
              )
            }
          })
          .catch((error) => {
            dispatch('setSeonData', {})
            logger.error(
              'Failed to retrieve SEON session data.',
              {
                orderInfo,
                error: error.message,
              },
              { category: 'seon' }
            )
          })
      }

      logSessionReplayGateStatus({
        order_id: orderInfo.orderId,
        checkout_id: checkoutId,
        merchant_name: merchantName,
        merchant_id: orderInfo.merchantId,
      })

      return orderInfo
    } catch (error) {
      const { status, data } = HttpRequestError.parse(error)

      if (status === 404) {
        const sessionErrorData = data?.data || {}
        const { merchantFailureUrl, merchantSuccessUrl, isOrderSuccess } =
          sessionErrorData

        const redirectUrl = isOrderSuccess
          ? merchantSuccessUrl
          : merchantFailureUrl

        commit('SET_REDIRECT_URL', redirectUrl)
        commit('SET_SESSION_ERROR_DATA', sessionErrorData)
      }

      commit('SET_STATUS', statuses.INVALID)
      // Clear checkoutId and order if it exists
      dispatch('setCheckoutId', '')
      dispatch('setOrder', {})
      dispatch('setSeonData', {})

      logger.debug(
        'Get order session error',
        { data: { checkoutId, data } },
        { category: 'session', 'order.checkoutId': checkoutId }
      )
      clientLogger.debug('Get order session error')

      logger.error(
        error,
        {
          data,
        },
        { category: 'session', 'order.checkoutId': checkoutId }
      )

      throw error
    }
  },

  cancelCheckoutSession({ state }) {
    // Receive checkoutId from arguments or state (Load from storage before that)
    const checkoutId = state.checkoutId

    if (!checkoutId) {
      return
    }

    // Trigger cancel checkout sesson on BE side. We don't wait this action
    return CheckoutSessionAPI.cancelCheckoutSession(checkoutId)
  },

  setCheckoutId({ commit }, checkoutId) {
    commit('SET_CHECKOUT_ID', checkoutId)
    commit('SET_STATUS', statuses.UNCHECK)

    setCheckoutId(checkoutId)
  },

  setDeviceId({ commit }, deviceId) {
    commit('SET_DEVICE_ID', deviceId)
    setDeviceId(deviceId)
  },

  setOrder({ commit, state }, orderInfo) {
    commit('SET_ORDER', orderInfo)

    const checkoutId = state.checkoutId

    // storage the latest session until that session finish
    Storage.setSessionData({
      ...orderInfo,
      checkoutId,
    })
  },

  updateInstallments({ dispatch, state, getters }, instalments) {
    if (instalments === getters.numberOfInstallment) {
      return
    }

    const orderInfo = { ...state.order, instalments }

    dispatch('setOrder', orderInfo)
  },

  updatePaymentType({ dispatch, state, getters }, paymentType) {
    if (paymentType === getters.paymentType) {
      return
    }

    const orderInfo = { ...state.order, paymentType }

    dispatch('setOrder', orderInfo)

    dispatch('payment/allowGetExistingCards', null, { root: true })
    dispatch('payment/getPaymentExistingCards', null, { root: true })
  },

  setAutoFillPhoneNumber({ commit }, value) {
    commit('SET_AUTO_FILL_PHONE_NUMBER', value)
  },

  // remove session
  resetSession({ commit }) {
    commit('SET_STATUS', statuses.UNCHECK)
    commit('SET_ORDER', {})
    commit('SET_REDIRECT_URL', '')
    commit('SET_SESSION_ERROR_DATA', {})

    removeCheckoutId()
    Storage.clearSessionData()
  },

  cleanUp({ dispatch }) {
    dispatch('resetSession')
  },

  setSeonData({ commit }, seonData) {
    commit('SET_SEON_DATA', seonData)
    Seon.setSeonData(seonData)
  },
}

export default {
  namespaced: true,
  state,
  getters,
  mutations,
  actions,
}
